// Contactverzoeken DTOs — Spec C §11
//
// Vervangt het oude `gesprekken.ts` (zie shim aldaar) met een uitgebreid
// model: 5 types (klantcontact / TL / HR / VP / Kasper), VP-afscherming,
// klantcontact-routing.

import { z } from 'zod'

// ─── Enums (gespiegeld vanuit Prisma) ────────────────────────────────────────

export const ContactRequestTypeEnum = z.enum([
  'CUSTOMER_CONTACT',
  'TEAMLEADER',
  'HR',
  'CONFIDENTIAL_ADVISOR',
  'KASPER',
])
export type ContactRequestType = z.infer<typeof ContactRequestTypeEnum>

export const ContactRequestPriorityEnum = z.enum([
  'LOW',
  'NORMAL',
  'HIGH',
  'URGENT',
])
export type ContactRequestPriority = z.infer<typeof ContactRequestPriorityEnum>

export const ContactRequestTermijnEnum = z.enum([
  'VANDAAG',
  'BINNEN_2_WERKDAGEN',
  'DEZE_WEEK',
  'GEEN_HAAST',
])
export type ContactRequestTermijn = z.infer<typeof ContactRequestTermijnEnum>

export const ContactRequestStatusEnum = z.enum([
  'OPEN',
  'GELEZEN',
  'INGEPLAND',
  'AFGEHANDELD',
  'GEANNULEERD',
])
export type ContactRequestStatus = z.infer<typeof ContactRequestStatusEnum>

export const KlantcontactRedenEnum = z.enum([
  'STORING_ESCALATIE',
  'ONDUIDELIJKHEID_OPDRACHT',
  'KLANTCONFLICT',
  'PLANNING',
  'VEILIGHEID',
  'OVERIG',
])
export type KlantcontactReden = z.infer<typeof KlantcontactRedenEnum>

// ─── Input schemas ───────────────────────────────────────────────────────────

export const CreateContactRequestSchema = z
  .object({
    type: ContactRequestTypeEnum,
    priority: ContactRequestPriorityEnum.optional().default('NORMAL'),
    gewensteTermijn: ContactRequestTermijnEnum.optional().default('GEEN_HAAST'),
    gewenstMoment: z.string().datetime().optional(),
    redenGedeeld: z.boolean(),
    reden: z.string().trim().max(1000).optional(),
    // CUSTOMER_CONTACT-specifiek
    clientId: z.string().min(1).optional(),
    clientContactId: z.string().min(1).optional(),
    klantcontactReden: KlantcontactRedenEnum.optional(),
    // CONFIDENTIAL_ADVISOR-specifiek
    vertrouwenspersoonId: z.string().min(1).optional(),
  })
  .refine((data) => !(data.redenGedeeld && !data.reden), {
    message: 'Reden is verplicht als je een reden wilt delen',
    path: ['reden'],
  })
  .refine((data) => !(data.type === 'CUSTOMER_CONTACT' && !data.clientId), {
    message: 'Kies een klant bij een klantcontact-verzoek',
    path: ['clientId'],
  })

export const ScheduleContactRequestSchema = z.object({
  ingeplandOp: z.string().datetime(),
  locatie: z.string().trim().max(200).optional(),
  interneNotitie: z.string().trim().max(2000).optional(),
})

export const CompleteContactRequestSchema = z.object({
  interneNotitie: z.string().trim().max(2000).optional(),
})

export const CancelContactRequestSchema = z.object({
  reden: z.string().trim().max(500).optional(),
})

export type CreateContactRequestInput = z.infer<typeof CreateContactRequestSchema>
export type ScheduleContactRequestInput = z.infer<typeof ScheduleContactRequestSchema>
export type CompleteContactRequestInput = z.infer<typeof CompleteContactRequestSchema>
export type CancelContactRequestInput = z.infer<typeof CancelContactRequestSchema>

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface ContactRequestDTO {
  id: string
  requesterId: string
  type: ContactRequestType
  priority: ContactRequestPriority
  gewensteTermijn: ContactRequestTermijn
  gewenstMoment: string | null
  redenGedeeld: boolean
  reden: string | null
  status: ContactRequestStatus
  gelezenOp: string | null
  ingeplandOp: string | null
  ingeplandLocatie: string | null
  afgehandeldOp: string | null
  geannuleerdOp: string | null
  // CUSTOMER_CONTACT
  clientId: string | null
  clientNaam: string | null
  clientContactNaam: string | null
  klantcontactReden: KlantcontactReden | null
  createdAt: string
  updatedAt: string
}

// Admin-view bevat extra metadata. NOOIT renderen voor VP-verzoeken.
export interface ContactRequestAdminDTO extends ContactRequestDTO {
  interneNotitie: string | null
  requester: { id: string; name: string | null; email: string } | null
  audits: ContactRequestAuditDTO[]
}

export interface ContactRequestAuditDTO {
  id: string
  actie: string
  actorId: string
  actorName: string | null
  details: string | null
  createdAt: string
}

// ─── NL-labels (UI) ──────────────────────────────────────────────────────────

export const CONTACT_TYPE_LABELS: Record<ContactRequestType, string> = {
  CUSTOMER_CONTACT: 'Klantcontactpersoon',
  TEAMLEADER: 'Teamleider',
  HR: 'Personeelszaken',
  CONFIDENTIAL_ADVISOR: 'Vertrouwenspersoon',
  KASPER: 'Kasper',
}

export const CONTACT_PRIORITY_LABELS: Record<ContactRequestPriority, string> = {
  LOW: 'Niet dringend',
  NORMAL: 'Normaal',
  HIGH: 'Belangrijk',
  URGENT: 'Urgent',
}

export const CONTACT_TERMIJN_LABELS: Record<ContactRequestTermijn, string> = {
  VANDAAG: 'Vandaag',
  BINNEN_2_WERKDAGEN: 'Binnen 2 werkdagen',
  DEZE_WEEK: 'Deze week',
  GEEN_HAAST: 'Geen haast',
}

export const CONTACT_STATUS_LABELS: Record<ContactRequestStatus, string> = {
  OPEN: 'Open',
  GELEZEN: 'Gelezen',
  INGEPLAND: 'Ingepland',
  AFGEHANDELD: 'Afgehandeld',
  GEANNULEERD: 'Geannuleerd',
}

export type ContactStatusBadgeVariant =
  | 'warning'
  | 'info'
  | 'primary'
  | 'success'
  | 'secondary'

export const CONTACT_STATUS_BADGE_VARIANT: Record<
  ContactRequestStatus,
  ContactStatusBadgeVariant
> = {
  OPEN: 'warning',
  GELEZEN: 'info',
  INGEPLAND: 'primary',
  AFGEHANDELD: 'success',
  GEANNULEERD: 'secondary',
}

export const KLANTCONTACT_REDEN_LABELS: Record<KlantcontactReden, string> = {
  STORING_ESCALATIE: 'Storing / escalatie',
  ONDUIDELIJKHEID_OPDRACHT: 'Onduidelijkheid opdracht',
  KLANTCONFLICT: 'Klantconflict',
  PLANNING: 'Planning',
  VEILIGHEID: 'Veiligheid',
  OVERIG: 'Overig',
}
